import { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import DocumentsService from "../../../../common/api/documentsApi";
import { EmptyListPlaceholderContent } from "../../../../shared/Ui/EmptyPageBlock";
import CopyButton from "../../../../shared/Ui/CopyButton/CopyButton";
import CheckBox from "../../../../shared/Ui/CheckBox/CheckBox";

export type DocumentMarkRow = {
  markId?: number;
  id?: number;
  markCode?: string;
  code?: string;
  kmType?: string;
  type?: string;
  packagingType?: string;
  innerKmCount?: number | null;
  productTypeCode?: string;
  productName?: string;
  ntinName?: string;
  ntinCodeName?: string;
  ntin?: string;
  orderNumber?: string;
  [key: string]: any;
};

type Props = {
  documentId?: string;
  rows: DocumentMarkRow[];
  pageIndex?: number;
  pageSize?: number;
  canEdit?: boolean;
  deleteMarks?: number[];
  onToggleDelete?: (markId: number) => void;
  onSelectAllDelete?: () => void;
  onDeleteOne?: (markId: number) => void;
  allSelected?: boolean;
};

const tdClass =
  "px-5 py-5 text-sm bg-white border-b border-gray-200 text-gray-600";
const thClass =
  "px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200";

const formatKmTypeLabel = (
  kmType: string | undefined | null,
  t: (key: string) => string,
) => {
  if (!kmType) return "-";
  const upper = String(kmType).toUpperCase();
  if (
    upper === "GROUP" ||
    upper.includes("ГРУПП") ||
    upper.includes("ТОПТ")
  ) {
    return t("new_order.km_type_group");
  }
  if (upper === "CONSUMER" || upper.includes("ПОТРЕБ") || upper.includes("ТҰТЫН")) {
    return t("new_order.km_type_consumer");
  }
  if (upper === "TRANSPORT" || upper.includes("ТРАНСП")) {
    return t("new_order.km_type_transport");
  }
  return String(kmType);
};

const isGroupMark = (row: DocumentMarkRow) => {
  const raw = row?.kmType ?? row?.type ?? row?.packagingType;
  if (raw == null || raw === "") return false;
  const upper = String(raw).toUpperCase();
  return (
    upper === "GROUP" ||
    upper.includes("ГРУПП") ||
    upper.includes("ТОПТ") ||
    upper.includes("GROUP")
  );
};

const getMarkId = (row: DocumentMarkRow) => Number(row?.markId ?? row?.id);

const DocumentMarksTable = ({
  documentId,
  rows,
  pageIndex = 0,
  pageSize = 10,
  canEdit = false,
  deleteMarks = [],
  onToggleDelete,
  onSelectAllDelete,
  onDeleteOne,
  allSelected = false,
}: Props) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [childrenMap, setChildrenMap] = useState<Record<number, DocumentMarkRow[]>>(
    {},
  );

  const colSpan = (canEdit ? 2 : 0) + 9;

  const toggleExpand = async (row: DocumentMarkRow) => {
    const markId = getMarkId(row);
    if (!Number.isFinite(markId) || !documentId) return;

    if (expanded[markId]) {
      setExpanded((prev) => ({ ...prev, [markId]: false }));
      return;
    }

    setExpanded((prev) => ({ ...prev, [markId]: true }));

    if (markId in childrenMap) return;

    try {
      const res = await DocumentsService.getDocMarkChildren(documentId, markId);
      const list = Array.isArray(res)
        ? res
        : res?.products || res?.marks || res?.data || [];
      setChildrenMap((prev) => ({ ...prev, [markId]: list }));
    } catch {
      setChildrenMap((prev) => ({ ...prev, [markId]: [] }));
    }
  };

  const renderDataCells = (
    row: DocumentMarkRow,
    indexLabel: string | number,
    depth: number,
  ) => {
    const markId = getMarkId(row);
    const group = isGroupMark(row);
    const isOpen = !!expanded[markId];
    const kmTypeRaw = row?.kmType ?? row?.type ?? row?.packagingType;

    return (
      <>
        {canEdit && (
          <td className={tdClass}>
            <CheckBox
              id={`doc-mark-${markId}-${depth}`}
              checked={deleteMarks.includes(markId)}
              onChange={() => onToggleDelete?.(markId)}
            />
          </td>
        )}
        <td className={tdClass}>{indexLabel}</td>
        <td
          className={`${tdClass} break-all text-left`}
          style={{ paddingLeft: 20 + depth * 16 }}
        >
          {row?.markCode || row?.code || "-"}
        </td>
        <td className={tdClass}>
          <div className="inline-flex items-center justify-center gap-2">
            {group && (
              <button
                type="button"
                aria-label="expand"
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-400 text-gray-600 transition-transform duration-200 hover:bg-gray-100 ${
                  isOpen ? "rotate-90" : ""
                }`}
                onClick={() => toggleExpand(row)}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 2L8 6L4 10"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            <span>{formatKmTypeLabel(kmTypeRaw, t)}</span>
          </div>
        </td>
        <td className={tdClass}>
          {row?.innerKmCount != null ? row.innerKmCount : "-"}
        </td>
        <td className={tdClass}>{row?.productTypeCode || "-"}</td>
        <td className={tdClass}>{row?.productName || "-"}</td>
        <td className={tdClass}>
          {row?.ntinName || row?.ntinCodeName || "-"}
        </td>
        <td className={tdClass}>
          <div className="inline-flex items-center justify-center gap-1">
            <span>{row?.ntin || "-"}</span>
            <CopyButton value={row?.ntin} />
          </div>
        </td>
        <td className={tdClass}>{row?.orderNumber || "-"}</td>
        {canEdit && (
          <td className={tdClass}>
            <img
              src="/icons/trash.svg"
              alt=""
              className="mx-auto min-w-[20px] cursor-pointer"
              onClick={() => onDeleteOne?.(markId)}
            />
          </td>
        )}
      </>
    );
  };

  const renderRowTree = (
    row: DocumentMarkRow,
    indexLabel: string | number,
    depth: number,
  ): ReactElement[] => {
    const markId = getMarkId(row);
    const nodes: ReactElement[] = [
      <tr key={`row-${markId}-${depth}-${indexLabel}`}>{renderDataCells(row, indexLabel, depth)}</tr>,
    ];

    if (!expanded[markId] || !(markId in childrenMap)) return nodes;

    const children = childrenMap[markId] || [];
    if (!children.length) {
      nodes.push(
        <tr key={`empty-${markId}-${depth}`}>
          <td
            colSpan={colSpan}
            className="px-5 py-4 text-sm bg-white border-b border-gray-200 text-gray-500 text-center"
          >
            {t("documents_orders.children_empty")}
          </td>
        </tr>,
      );
      return nodes;
    }

    children.forEach((child, i) => {
      nodes.push(
        ...renderRowTree(child, `${indexLabel}.${i + 1}`, depth + 1),
      );
    });
    return nodes;
  };

  return (
    <div className="w-full rounded-lg overflow-x-auto bg-white">
      <table className="max-w-full w-full leading-normal">
        <thead className="border-b-2 border-gray-300">
          <tr>
            {canEdit && (
              <th className={thClass} scope="col">
                <CheckBox
                  checked={allSelected}
                  onChange={() => onSelectAllDelete?.()}
                />
              </th>
            )}
            <th className={thClass} scope="col">
              № {t("documents_orders.number")}
            </th>
            <th className={thClass} scope="col">
              {t("documents_orders.code")}
            </th>
            <th className={thClass} scope="col">
              {t("documents_orders.package_type")}
            </th>
            <th className={thClass} scope="col">
              {t("documents_orders.inner_km_count")}
            </th>
            <th className={thClass} scope="col">
              {t("documents_orders.ntin")}
            </th>
            <th className={thClass} scope="col">
              {t("documents_orders.product_name")}
            </th>
            <th className={thClass} scope="col">
              {t("documents_orders.ntin_name")}
            </th>
            <th className={thClass} scope="col">
              {t("documents_orders.ntin_code")}
            </th>
            <th className={thClass} scope="col">
              {t("documents_orders.order_number")}
            </th>
            {canEdit && <th className={thClass} scope="col" />}
          </tr>
        </thead>
        {!rows?.length ? (
          <tbody>
            <tr>
              <td colSpan={colSpan} className="text-center py-5">
                <EmptyListPlaceholderContent
                  title={t("documents_orders.no_data_title")}
                  description={t("documents_orders.no_data_description")}
                />
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody className="font-normal text-center">
            {rows.flatMap((row, index) =>
              renderRowTree(
                row,
                pageIndex * pageSize + index + 1,
                0,
              ),
            )}
          </tbody>
        )}
      </table>
    </div>
  );
};

export default DocumentMarksTable;
